import { useCallback, useEffect, useMemo, useState } from "react";
import cn from "classnames";
import type { RateImportSession } from "../../api/rates.types";
import { mapRate } from "./rates.map";
import type { PricingRule, RateIssueCode, RateRecord, RatesStep } from "./rates.model";
import { applyRateEdit, countByStatus, RATE_STEPS } from "./rates.model";
import { RatesImport } from "./RatesImport";
import { RatesLibrary } from "./RatesLibrary";
import { RatesOverview } from "./RatesOverview";
import { type RatesParseMeta, RatesParsingSummary } from "./RatesParsingSummary";
import { RatesPricing } from "./RatesPricing";
import { RatesPublish } from "./RatesPublish";

type RatesPaneProps = {
  tab: string;
  onSelectTab: (tab: RatesStep) => void;
};

const isStep = (tab: string): tab is RatesStep =>
  tab === "overview" || tab === "import" || tab === "library" || tab === "pricing" || tab === "published";

const EMPTY_PARSE_META: RatesParseMeta = { parsing: false, parsed: false, fixed: [] };

/**
 * Logistics Data: overview of active rates, then the four-step import workflow.
 * Desktop layout: main pane | sidebar (stepper + Parsing Summary under it).
 * Steps 2–4 stay empty/disabled until a real QSearch parse fills rates (no SEED demo data).
 */
export const RatesPane = ({ tab, onSelectTab }: RatesPaneProps) => {
  const [rates, setRates] = useState<RateRecord[]>([]);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [rules, setRules] = useState<PricingRule[]>([]);
  const [parseMeta, setParseMeta] = useState<RatesParseMeta>(EMPTY_PARSE_META);
  const step: RatesStep = isStep(tab) ? tab : "overview";
  const counts = useMemo(() => countByStatus(rates), [rates]);

  const stepEnabled: Record<RatesStep, boolean> = {
    overview: true,
    import: true,
    library: counts.all > 0,
    pricing: counts.ready > 0,
    published: counts.ready > 0,
  };

  useEffect(() => {
    if (step !== "overview" && step !== "import" && !stepEnabled[step]) {
      onSelectTab("import");
    }
  }, [step, stepEnabled.library, stepEnabled.pricing, stepEnabled.published, onSelectTab]);

  const editRate = (next: RateRecord) =>
    setRates((current) => current.map((rate) => (rate.id === next.id ? applyRateEdit(rate, next) : rate)));

  const deleteRate = (id: string) => setRates((current) => current.filter((rate) => rate.id !== id));

  const onSession = (session: RateImportSession) => {
    setSessionId(session.id);
    setRates(session.rates.map(mapRate));
  };

  const onParseMeta = useCallback((meta: { parsing: boolean; parsed: boolean; fixed: RateIssueCode[] }) => {
    setParseMeta(meta);
  }, []);

  if (step === "overview") {
    return <RatesOverview rates={rates} onAdd={() => onSelectTab("import")} />;
  }

  return (
    <div className="rt-workflow">
      <button
        type="button"
        className="rt-back"
        aria-label="Back to Logistics Data overview"
        onClick={() => onSelectTab("overview")}
      >
        <span className="rt-back__chev" aria-hidden="true" />
      </button>

      <div className="pc-pane pc-pane--rates">
        <div className="nw-tabs nw-tabs--4 nw-tabs--flow" role="tablist">
          {RATE_STEPS.map((item) => {
            const enabled = stepEnabled[item.key];
            const title =
              item.key === "library" && !enabled
                ? "Parse a tariff first — Data Review opens when rates appear"
                : item.key === "pricing" && !enabled
                  ? "Need at least one Clean rate before Pricing Rules"
                  : item.key === "published" && !enabled
                    ? "Need at least one Clean rate before Approve & Publish"
                    : undefined;

            return (
              <button
                key={item.key}
                type="button"
                className={cn("nw-tabs__b", { "is-on": step === item.key, "is-disabled": !enabled })}
                role="tab"
                aria-selected={step === item.key}
                aria-disabled={!enabled}
                disabled={!enabled}
                title={title}
                onClick={() => {
                  if (!enabled) return;
                  onSelectTab(item.key);
                }}
              >
                <em className="rt-step">{item.step}</em>
                {item.label}
              </button>
            );
          })}
        </div>

        <RatesParsingSummary rates={rates} meta={parseMeta} />

        {step === "library" && stepEnabled.library && (
          <RatesLibrary rates={rates} onEdit={editRate} onDelete={deleteRate} />
        )}
        {step === "pricing" && stepEnabled.pricing && (
          <RatesPricing
            rules={rules}
            onAdd={(rule) => setRules((current) => [rule, ...current])}
            onRemove={(id) => setRules((current) => current.filter((rule) => rule.id !== id))}
          />
        )}
        {step === "published" && stepEnabled.published && <RatesPublish rates={rates} onDelete={deleteRate} />}
        <RatesImport
          rates={rates}
          sessionId={sessionId}
          onSession={onSession}
          onParseMeta={onParseMeta}
          hidden={step !== "import"}
          onGoLibrary={() => {
            if (stepEnabled.library) onSelectTab("library");
          }}
        />
      </div>
    </div>
  );
};
